import { Directive, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from "@angular/core";
import { Subscription } from "rxjs";

import { PermissionService } from "../services/auth/permission.service";

/**
 * Structural directive: renders its host template only while the caller holds the given RBAC
 * permission id, reactively.
 *
 * `<button *hasPermission="'consent.write'">Save</button>` — the button is created when
 * `PermissionService.has$('consent.write')` emits true and removed when it emits false, so a
 * denied->granted change (e.g. once `PermissionService.load()` resolves after login) shows the
 * button without a page reload, and vice versa.
 *
 * UX only, never an authorization boundary (dashboard-security skill, rule #1): hiding a control
 * here is a convenience. The API is the real check and re-validates every write regardless of
 * what this directive rendered. It is fine for this to be the *only* thing gating a button's
 * visibility — it must never be the only thing gating the action the button triggers.
 *
 * The selector intentionally does not carry the repo's "dir" attribute-directive prefix: a
 * structural directive's input name must equal its selector for Angular's `*hasPermission="..."`
 * microsyntax to resolve (the same reason `NgIf`'s own input is named `ngIf`, not `dirNgIf`).
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector -- structural directive: the
  // selector must match the input name below for `*hasPermission="'id'"` template sugar to bind;
  // the "dir" prefix convention in .eslintrc.json targets attribute directives, not this pattern.
  selector: "[hasPermission]",
  standalone: true
})
export class HasPermissionDirective implements OnInit, OnDestroy {
  @Input() public hasPermission: string = "";

  private viewCreated: boolean = false;
  private subscription: Subscription | undefined;

  public constructor(
    private readonly templateRef: TemplateRef<unknown>,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly permissionService: PermissionService
  ) {}

  public ngOnInit(): void {
    this.subscription = this.permissionService.has$(this.hasPermission).subscribe((granted: boolean) => {
      this.updateView(granted);
    });
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  /** Guards against double-create/double-clear so repeated emissions of the same value are no-ops. */
  private updateView(granted: boolean): void {
    if (granted && !this.viewCreated) {
      this.viewContainerRef.createEmbeddedView(this.templateRef);
      this.viewCreated = true;
    } else if (!granted && this.viewCreated) {
      this.viewContainerRef.clear();
      this.viewCreated = false;
    }
  }
}
